import { ReactNode, useCallback, useLayoutEffect } from "react";
import {
  List,
  Paper,
  styled,
  ListItem,
  ClickAwayListener,
  Tooltip,
} from "@mui/material";
import { Flex } from "~/components/Flex";
import { Text, TextCaption } from "~/components/ui-library";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  AuthorType,
  ExceptionReviewAction,
  ListExceptionGroupsDocument,
  ExceptionStatus,
} from "~/operations";
import { FormatDate } from "~/lib/date";
import {
  AccessTime as AccessTimeIcon,
  PersonOutline as PersonOutlineIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { ExceptionTypeIcon } from "~/pages/findings/Exceptions/components/ExceptionTypeIcon";
import ExceptionButtons from "~/pages/findings/Exceptions/components/ExceptionButtons";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { ExceptionTarget } from "~/components/exceptions/types";
import { ExceptionGroup } from "~/pages/findings/Exceptions/types";
import { RemoveExceptionsGroupDialogPayload } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import { spaceIdFromMrn, useScope } from "~/hooks/useScope";
import { SpacesIcon } from "~/icons";

export type ExceptionTimelineItemProps = {
  exceptionGroup: ExceptionGroup;
  children?: ReactNode;
  onReviewActionChange?: (
    value: ExceptionGroup,
    action: ExceptionReviewAction,
  ) => void;
  renderTable: (exceptionGroup: ExceptionGroup) => ReactNode;
  isReviewLoading: boolean;
  loadingAction: ExceptionReviewAction | undefined;
  loadingExceptionId: string | undefined;
  target?: ExceptionTarget;
  displayScope?: boolean;
};

const getBorderColor = (isFocused: boolean, isPendingException: boolean) => {
  if (isFocused) return "primary.light";
  if (isPendingException) return "warning.main";
  return "transparent";
};

export const ExceptionTimelineItem = ({
  exceptionGroup,
  children,
  onReviewActionChange,
  renderTable,
  isReviewLoading = false,
  loadingAction,
  loadingExceptionId,
  target,
  displayScope,
}: ExceptionTimelineItemProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { spaceScope } = useScope();

  const isThisExceptionLoading =
    isReviewLoading && loadingExceptionId === exceptionGroup?.id;

  const isPendingException = exceptionGroup?.status === ExceptionStatus.New;

  const isFocused = searchParams.get("exceptionId") === exceptionGroup.id;

  useLayoutEffect(() => {
    if (isFocused) {
      document
        .getElementById(exceptionGroup.id)
        ?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isFocused]);

  const handleClickAway = useCallback(() => {
    if (!isFocused) return;
    searchParams.delete("exceptionId");
    setSearchParams(searchParams);
  }, [isFocused]);

  const { handleRemoveExceptionGroups } = useExceptions({
    scopeMrns: [exceptionGroup.scopeMrn],
    refetchQueries: [ListExceptionGroupsDocument],
  });

  if (!spaceScope) return;

  const spaceMrn = spaceScope.mrn;
  const spaceId = spaceIdFromMrn(spaceMrn);

  const removeExceptionsGroupDialogPayload:
    | RemoveExceptionsGroupDialogPayload
    | undefined =
    target || isPendingException
      ? {
          onRemove: handleRemoveExceptionGroups,
          exceptionMrns: [exceptionGroup.mrn],
        }
      : undefined;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <List
        id={exceptionGroup.id}
        component={Paper}
        sx={(theme) => ({
          py: 0,
          mb: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: getBorderColor(isFocused, isPendingException),
          scrollMarginTop: theme.spacing(3),
        })}
      >
        <ListItemContainer>
          <Flex flexDirection="column" flex={1}>
            <Flex justifyContent="space-between">
              <Flex center gap={2}>
                <ExceptionTypeIcon action={exceptionGroup.action} />
                <Text bold sx={{ textTransform: "uppercase" }}>
                  {exceptionGroup.title}
                </Text>
              </Flex>
              <ExceptionButtons
                exceptionGroup={exceptionGroup}
                onReviewActionChange={onReviewActionChange}
                isLoading={isThisExceptionLoading}
                loadingAction={loadingAction}
                removeExceptionGroupPayload={removeExceptionsGroupDialogPayload}
              />
            </Flex>
            <Flex justifyContent="space-between" pt={2} pb={2}>
              <Flex gap={3}>
                {displayScope && (
                  <Flex center gap={1}>
                    <SpacesIcon fontSize="small" />
                    <TextCaption bold secondary>
                      {spaceScope.name}
                    </TextCaption>
                  </Flex>
                )}
                {exceptionGroup.author && (
                  <Tooltip
                    title={`Created by: ${exceptionGroup.author.name}`}
                    arrow
                    placement="top"
                  >
                    <Flex center gap={1}>
                      <PersonOutlineIcon fontSize="small" />
                      <TextCaption
                        bold
                        secondary
                        {...(exceptionGroup.author.type ===
                          AuthorType.ServiceAccount && {
                          component: RouterLink,
                          to: `/space/settings/service-accounts?spaceId=${spaceId}&queryTerms=${exceptionGroup.author.name}`,
                          sx: {
                            color: "primary.light",
                          },
                        })}
                      >
                        {exceptionGroup.author.name}
                      </TextCaption>
                    </Flex>
                  </Tooltip>
                )}
                <Tooltip
                  title={`Created: ${FormatDate(exceptionGroup.createdAt)}`}
                  arrow
                  placement="top"
                >
                  <Flex center gap={1}>
                    <AccessTimeIcon fontSize="small" />
                    <TextCaption bold secondary>
                      {FormatDate(exceptionGroup.createdAt)}
                    </TextCaption>
                  </Flex>
                </Tooltip>
              </Flex>
              {exceptionGroup.reviewer && (
                <Flex gap={3}>
                  <Tooltip
                    title={`Reviewed by: ${exceptionGroup.reviewer.name}`}
                    arrow
                    placement="top"
                  >
                    <Flex center gap={1}>
                      <VisibilityIcon fontSize="small" />
                      <TextCaption bold secondary>
                        {exceptionGroup.reviewer.name}
                      </TextCaption>
                    </Flex>
                  </Tooltip>
                  <Tooltip
                    title={`Reviewed: ${FormatDate(exceptionGroup.modifiedAt)}`}
                    arrow
                    placement="top"
                  >
                    <Flex center gap={1}>
                      <AccessTimeIcon fontSize="small" />
                      <TextCaption bold secondary>
                        {FormatDate(exceptionGroup.modifiedAt)}
                      </TextCaption>
                    </Flex>
                  </Tooltip>
                </Flex>
              )}
            </Flex>
            {exceptionGroup.justification && (
              <Text secondary mb={1}>
                {exceptionGroup.justification}
              </Text>
            )}
            {renderTable(exceptionGroup)}
            {children}
          </Flex>
        </ListItemContainer>
      </List>
    </ClickAwayListener>
  );
};

const ListItemContainer = styled(ListItem)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(3),
}));
